import { DecimalPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { RankingMedicosConsultasEletivasGuias, VinculoUsuarioOperadora } from '@gestor-web/@core/api/ords/model';
import { LayoutActions, OperadorasActions } from '@gestor-web/@core/store/actions';
import { LayoutSelectors, OperadorasSelectors } from '@gestor-web/@core/store/selectors';
import { filterToQueryString, pseudonimizar, pseudonimizarPrimeiroNome } from '@gestor-web/@core/utils';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { CompetenciaPipe } from '@gestor-web/app/shared/pipes/competencia.pipe';
import { CpfCnpjPipe } from '@gestor-web/app/shared/pipes/cpf-ou-cnpj.pipe';
import { GeralService, NotificationService, TipoDominio, PermissaoService } from '@gestor-web/app/shared/services';
import { GridService } from '@gestor-web/app/shared/services/grid.service';
import { AgGridAngular } from 'ag-grid-angular';
import { AgGridEvent, ColDef } from 'ag-grid-community';
import { GridOptions } from 'ag-grid-enterprise';
import { AgFilterUtils, buildDefaultColumnDef, buildGridOptions, exportGridAsExcel } from '@gestor-web/app/shared/components';
import { SelectItem } from 'primeng/api';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import { first, map, mergeMap, takeUntil } from 'rxjs/operators';
import { GuiaMonitorActionCellRenderer } from '@gestor-web/app/pages/private/criticas/criticas-geradas/guia-monitor-action-cell.component';
import { FilterData } from '../../components/filtro/filtro.component';
import { FUNCIONALIDADE } from '@gestor-web/@core/auth/funcionalidades';

import { RankingMedicosConsultasCellRenderer } from './ranking-medicos-consultas-action-cell.component';
import {
  RankingMedicosConsultasEletivasComVerificacao,
  RankingMedicosConsultasService,
} from './ranking-medicos-consultas.service';

const DIALOG_PADDING_MAX_OFF = 135;
const DIALOG_PADDING_MAX_ON = 35;

const GRID_KEY = 'ANALISES.RANKING_MEDICOS_CONSULTAS';
const GRID_DIALOG_KEY = 'ANALISES.RANKING_MEDICOS_CONSULTAS.GUIAS';

const COLUMNS: ColDef[] = [
  {
    headerName: 'CPF do médico',
    field: 'nr_cpf_profissional',
    width: 100,
    type: 'cpfCnpj',
  },
  {
    headerName: 'Nome do médico',
    field: 'nm_medico',
    width: 100,
    // sortIndex: 2,
  },
  {
    headerName: 'CBO / Especialidade do Médico no CFM',
    field: 'lt_cbo_especialidade_medico_conselho',
    width: 100,
  },
  {
    headerName: 'CRM/UF do Médico',
    field: 'lt_crm_uf_medico',
    width: 100,
  },
  {
    headerName: 'Qtde total de consultas',
    field: 'qt_consulta',
    type: 'int',
    // sortIndex: 0,
    width: 100,
    cellRenderer: RankingMedicosConsultasCellRenderer,
  },
  {
    headerName: 'Qtde de CBO por profissional nas guias',
    field: 'qt_cbo_exec',
    type: 'int',
    // sortIndex: 1,
    width: 75,
  },
  {
    headerName: 'Qtd. consultas por CBO do médico (nas guias)',
    field: 'lista_cbo_exec_guias',
    width: 300,
    //maxWidth: 300,
  },
  {
    headerName: 'Verificação',
    field: 'ds_verificacao',
    width: 300,
  },

  // Hidden
  {
    headerName: 'Código do CBO (conforme habilitação no CFM)',
    field: 'lt_cd_cbo_especialidade_medico_conselho',
    hide: true,
  },
  {
    headerName: 'Código do CBO preenchido nas guias',
    field: 'lt_cd_cbo_exec_guias',
    hide: true,
  },
];

const COLUMNS_GUIAS: ColDef[] = [
  {
    headerName: '003-Competência',
    field: 'nr_competencia',
    type: 'competencia',
    width: 100,
  },
  {
    headerName: 'Cód. lancto',
    field: 'cd_guia_monitor',
    cellRenderer: GuiaMonitorActionCellRenderer,
    width: 150,
  },
  {
    headerName: '012-CNES Executante',
    field: 'nr_cnes_exec',
    type: 'str',
    width: 100,
  },
  {
    headerName: '014-CNPJ/CPF Executante.',
    field: 'nr_cnpj_cpf_exec',
    width: 100,
    type: 'cpfCnpj',
  },
  {
    headerName: 'Regans prestador exec.',
    field: 'cd_registro_ans_prestador_exec',
    type: 'str',
  },
  {
    headerName: 'Nome OPS prestador exec.',
    field: 'sg_operadora_prestador_exec',
  },
  {
    headerName: '081-Registro ANS da OPS intermediária',
    field: 'nr_registro_ans_intermediaria',
    type: 'str',
  },
  {
    headerName: 'Nome OPS intermediária',
    field: 'sg_operadora_prestador_imetermediaria',
  },
  {
    headerName: '024-Nr. guia operadora',
    field: 'nr_guia_operadora',
    type: 'str',
    width: 100,
  },
  {
    headerName: 'Nr. guia prestador',
    field: 'nr_guia_prestador',
    type: 'str',
    width: 100,
  },
  {
    headerName: '025-ID reembolso ou pagto eventual',
    field: 'nr_reembolso',
    type: 'str',
    width: 150,
  },
  {
    headerName: 'Tipo de rede',
    field: 'nm_tp_rede',
    width: 100,
  },
  {
    headerName: 'Sit. registro',
    field: 'nm_id_situacao_registro',
  },
  {
    headerName: '035-Cód. CBO executante',
    field: 'nr_cbo_exec',
  },

  // Hidden
  {
    headerName: '006-Registro ANSs',
    field: 'cd_registro_ans',
    hide: true,
  },
  {
    headerName: '013-Tipo de identificação - CPF/CNPJ',
    field: 'id_cnpj_cpf_exec',
    hide: true,
  },
];

@Component({
    templateUrl: './ranking-medicos-consultas.component.html',
    styleUrls: ['./ranking-medicos-consultas.component.scss'],
    providers: [RankingMedicosConsultasService, DecimalPipe],
    standalone: false
})
export class RankingMedicosConsultasComponent implements OnInit, OnDestroy {
  readonly GRID_KEY = GRID_KEY;
  readonly GRID_DIALOG_KEY = GRID_DIALOG_KEY;

  @ViewChild('agGrid') agGrid: AgGridAngular<Row>;
  @ViewChild('agGridDialog') agGridDialog: AgGridAngular<RowGuias>;

  gridOptions: GridOptions<Row>;
  gridDialogOptions: GridOptions<RowGuias>;

  pageReady$ = new Subject<void>();
  filterReady$ = new Subject<boolean>();
  firstLoad = true;
  filterChanged = true;

  podeGerenciarVisoes = false;

  operadora: string;
  operadoraNome: string;
  competenciaRange: string[];

  anosBasesOptions: number[] = [];
  anoBase: number = null;

  faixasEtariasOptions: SelectItem<number>[] = [];
  faixaEtaria: number = null;

  filterData: FilterData = {
    nr_cnpj_cpf_exec: null,
    tp_rede: null,
  };

  rowData: Row[];
  rowDataOriginal: Row[];

  pseudonimizarAction: boolean = true;
  pseudonimizarActionVisible: boolean = true;

  medicoCpfMap: { [key: string]: Row } = {};
  medicoCpfList: string[] = [];

  selectedRow: Row;
  selectedRowIndex: number;

  dialogDisplay = false;
  dialogData: RowGuias[];
  dialogDataOriginal: RowGuias[] = [];
  dialogSubtitle = '';

  dialogHeightPadding = DIALOG_PADDING_MAX_OFF;

  detalheGuiaDisplay = false;
  detalheGuiaCodigo: string;

  pageTooltip = GRID_KEY;

  fullscreen$: Observable<boolean>;
  gridHeight$: Observable<string>;

  constructor(
    private permissaoService: PermissaoService,
    private service: RankingMedicosConsultasService,
    private geralService: GeralService,
    private decimalPipe: DecimalPipe,
    private cpfCnpjPipe: CpfCnpjPipe,
    private competenciaPipe: CompetenciaPipe,
    private notificationService: NotificationService,
    private gridService: GridService,
    private store: Store,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    this.fullscreen$ = this.store.select(LayoutSelectors.selectFullscreen);
    this.gridHeight$ = of('100%');
  }

  ngOnInit(): void {
    const ano = new Date().getFullYear();
    this.competenciaRange = [`${ano}01`, `${ano}12`];

    combineLatest([
      this.permissaoService.checkAcessoFeature(FUNCIONALIDADE.DESLIGAR_PSEUDONIMIZACAO),
      this.store.select(OperadorasSelectors.selectWorkspaceOperadora),
    ])
      .pipe(
        concatLatestFrom(() => this.store.select(OperadorasSelectors.selectWorkspaceParams)),
        mergeMap(([[podeDesligarPseudonimizacao, operadora], params]) =>
          this.geralService
            .getAnosBases(operadora.cd_registro_ans)
            .pipe(map(anosBases => ({ podeDesligarPseudonimizacao, operadora, params, anosBases }))),
        ),
        mergeMap(({ podeDesligarPseudonimizacao, operadora, params, anosBases }) =>
          this.geralService
            .getDominioPorTipo(TipoDominio.IND_FAIXA_ETARIA_INDICADOR_CONSULTA)
            .pipe(map(faixasEtarias => ({ podeDesligarPseudonimizacao, operadora, params, anosBases, faixasEtarias }))),
        ),
        takeUntil(this.ngDestroyed$),
      )
      .subscribe(({ podeDesligarPseudonimizacao, operadora, params, anosBases, faixasEtarias }) => {
        this.operadora = operadora.cd_registro_ans;
        this.operadoraNome = operadora.sg_operadora;
        this.competenciaRange = [
          params.competencia ? '' + params.competencia : null,
          params.competencia_final ? '' + params.competencia_final : null,
        ];
        this.anosBasesOptions = anosBases;
        if (anosBases.length) {
          let lastAno = anosBases[0];
          if (lastAno > ano && anosBases.find(a => a == ano)) {
            lastAno = ano;
          }
          setTimeout(() => {
            this.anoBase = lastAno;
          });
        }

        this.faixasEtariasOptions = faixasEtarias.map(item => ({
          value: +item.cd_valor_valido,
          label: item.ds_exibicao,
        }));
        this.faixaEtaria = this.faixasEtariasOptions.length ? this.faixasEtariasOptions[0].value : null;

        if (operadora.ib_dado_sensivel_pseudonimizado) {
          this.pseudonimizarActionVisible = false;
        } else {
          const vinculo = operadora as VinculoUsuarioOperadora;
          this.pseudonimizarActionVisible = podeDesligarPseudonimizacao || !vinculo.ib_dado_sensivel_pseudonimizado_usuario;
        }

        setTimeout(() => this.pageReady$.next());
      });

    combineLatest([this.route.queryParams, this.pageReady$])
      .pipe(first())
      .subscribe(([params, _]) => this.applyQueryFilters(params));

    combineLatest([this.filterReady$, this.pageReady$])
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(([hasFilter, _]) => {
        if (!this.firstLoad || hasFilter) {
          this.reloadData();
        }
      });

    this.permissaoService
      .checkAcessoFeature(FUNCIONALIDADE.GERENCIAR_VISOES)
      .pipe(takeUntil(this.ngDestroyed$))
      .subscribe(podeGerenciarVisoes => {
        this.podeGerenciarVisoes = podeGerenciarVisoes;
        this.configureGrid();
      });
  }

  private configureGrid() {
    this.gridOptions = buildGridOptions<Row>(
      {
        columnDefs: COLUMNS,
        pipes: {
          decimal: this.decimalPipe,
          cpfCnpj: this.cpfCnpjPipe,
        },
        views: {
          gridKey: GRID_KEY,
          hide: !this.podeGerenciarVisoes,
        },
        exportExcelFn: () => this.exportExcel(),
      },
      {
        context: {
          parentComponent: this,
        },
        defaultColDef: buildDefaultColumnDef({
          cellClassRules: {
            'cell-yellow': params => !params.api.isPivotMode() && params.data?.id_verificacao == 1,
            'cell-red': params => !params.api.isPivotMode() && params.data?.id_verificacao == 2,
          },
        }),
        excelStyles: [
          { id: 'cell-yellow', interior: { color: '#ffe699', pattern: 'Solid' } },
          { id: 'cell-red', interior: { color: '#ff8181', pattern: 'Solid' } },
        ],
        onFirstDataRendered: event => this.updateMedicoCpfList(event),
        onRowDataUpdated: event => this.updateMedicoCpfList(event),
        onFilterChanged: event => this.updateMedicoCpfList(event),
        onSortChanged: event => this.updateMedicoCpfList(event),
      },
    );

    this.gridDialogOptions = buildGridOptions<RowGuias>(
      {
        columnDefs: COLUMNS_GUIAS,
        pivotMode: false,
        pipes: {
          decimal: this.decimalPipe,
          competencia: this.competenciaPipe,
          cpfCnpj: this.cpfCnpjPipe,
        },
        views: {
          gridKey: GRID_DIALOG_KEY,
          hide: !this.podeGerenciarVisoes,
        },
        exportExcelFn: () => this.exportExcelDialog(),
      },
      {
        rowGroupPanelShow: 'never',
        context: {
          parentComponent: this,
        },
      },
    );
  }

  applyQueryFilters(params: Params) {
    const { competencia, competencia_final, anoBase, faixaEtaria, visao, sort, extraFilters, ...filterData } = params;
    let hasFilter = false;

    if (competencia) {
      hasFilter = true;
      this.competenciaRange[0] = competencia;
    }

    if (competencia_final) {
      hasFilter = true;
      this.competenciaRange[1] = competencia_final;
    }

    if (anoBase) {
      hasFilter = true;
      this.anoBase = +anoBase;
    }

    if (faixaEtaria) {
      hasFilter = true;
      this.faixaEtaria = +faixaEtaria;
    }

    if (filterData && Object.keys(filterData).length) {
      hasFilter = true;
    }
    this.filterData = { ...this.filterData, ...filterData };

    if (sort || extraFilters) {
      hasFilter = true;
    }

    const filterModel = this.gridService.getDecodedFilter(extraFilters);
    setTimeout(() => this.gridService.applyVisaoInicial(GRID_KEY, this.agGrid, visao, sort, filterModel));

    // Remove filters from url
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {},
    });

    this.filterReady$.next(hasFilter);
  }

  reloadData() {
    this.filterChanged = false;

    // Trata o comportamento do componente visual do carregamento
    if (this.firstLoad) {
      this.firstLoad = false;
      this.agGrid?.api?.setGridOption('loading', false);
      setTimeout(() => {
        this.agGrid?.api?.setGridOption('loading', true);
      });
    } else {
      this.agGrid?.api?.setGridOption('loading', true);
    }

    if (!this.competenciaRange[0] || !this.competenciaRange[1]) {
      this.rowData = [];
      this.rowDataOriginal = [];
      this.agGrid?.api?.setGridOption('pinnedBottomRowData', []);
      return;
    }

    this.service
      .getData(
        this.operadora,
        this.competenciaRange[0],
        this.competenciaRange[1],
        this.anoBase,
        this.faixaEtaria,
        this.filterData,
      )
      .subscribe({
        next: items => {
          this.medicoCpfMap = {};

          if (!items?.length) {
            this.rowData = [];
            this.rowDataOriginal = [];
            this.agGrid?.api?.setGridOption('pinnedBottomRowData', []);
            return;
          }

          this.rowDataOriginal = this.service.doVerificacao(items);
          this.pseudonimizarData();

          this.medicoCpfMap = this.rowData.reduce((acc, item) => {
            acc[item.cd_guia_monitor] = item;
            return acc;
          }, {});
          setTimeout(() => this.updateMedicoCpfList());

          const totalData = this.rowData.reduce(
            (acc, item) => ({
              nr_cpf_profissional: null,
              nm_medico: null,
              lt_cbo_especialidade_medico_conselho: null,
              lt_cd_cbo_especialidade_medico_conselho: null,
              lt_crm_uf_medico: null,
              qt_consulta: acc.qt_consulta + item.qt_cbo_exec,
              qt_cbo_exec: acc.qt_cbo_exec + item.qt_cbo_exec,
              lista_cbo_exec_guias: null,
              lt_cd_cbo_exec_guias: null,
              ds_verificacao: null,
              id_verificacao: 0,
            }),
            {
              qt_consulta: 0,
              qt_cbo_exec: 0,
            },
          );

          this.agGrid?.api?.setGridOption('pinnedBottomRowData', [totalData]);
        },
        error: error => this.notificationService.handleError(error),
        complete: () => this.agGrid?.api?.setGridOption('loading', false),
      });
  }

  competenciaChanged(range: string[]) {
    this.competenciaRange = range;
    this.store.dispatch(
      OperadorasActions.changeWorkspaceParams({
        partialParams: {
          competencia: +range[0],
          competencia_final: +range[1],
        },
      }),
    );
    // this.reloadData();
    this.filterChanged = true;
  }

  private updateMedicoCpfList(event?: AgGridEvent) {
    // console.log('[RankingMedico] updateMedicoCpfList', event);
    this.medicoCpfList = [];
    const api = event?.api ?? this.agGrid.api;
    api.forEachNodeAfterFilterAndSort((node, index) => {
      if (node.data) {
        if (!this.medicoCpfList.includes(node.data.cd_guia_monitor)) {
          this.medicoCpfList.push(node.data.cd_guia_monitor);
        }
      }
    });
    // console.log('[RankingMedico] updateMedicoCpfList - ', this.medicoCpfList);
  }

  private updateDialogSubtitle() {
    if (!this.selectedRow) {
      this.dialogSubtitle = '';
      return;
    }

    const { nr_cpf_profissional, nm_medico } = this.selectedRow;

    if (!this.pseudonimizarActionVisible || !this.pseudonimizarAction) {
      this.dialogSubtitle = nr_cpf_profissional
        ? `${this.cpfCnpjPipe.transform(nr_cpf_profissional)} - ${nm_medico}`
        : nm_medico;
      return;
    }

    const cpf = nr_cpf_profissional ? pseudonimizar(nr_cpf_profissional) : null;
    const nome = nm_medico ? pseudonimizarPrimeiroNome(nm_medico) : null;

    this.dialogSubtitle = cpf ? `${cpf} - ${nome}` : nome;
  }

  toggleFullscreen() {
    this.store.dispatch(LayoutActions.toggleFullscreen({}));
  }

  togglePseudonimizar() {
    this.pseudonimizarAction = !this.pseudonimizarAction;
    this.pseudonimizarData();
    this.pseudonimizarDialogData();
    this.updateDialogSubtitle();
  }

  private pseudonimizarData() {
    if (!this.pseudonimizarActionVisible || !this.pseudonimizarAction) {
      this.rowData = this.rowDataOriginal;
      return;
    }

    this.rowData = this.rowDataOriginal?.map(row => {
      return {
        ...row,

        ...(row.lt_crm_uf_medico
          ? {
              nr_cpf_profissional: pseudonimizar(row.nr_cpf_profissional),
              nm_medico: pseudonimizarPrimeiroNome(row.nm_medico),
            }
          : {}),
      };
    });
  }

  private pseudonimizarDialogData() {
    if (!this.dialogDataOriginal?.length) {
      this.dialogData = [];
      return;
    }

    if (!this.pseudonimizarActionVisible || !this.pseudonimizarAction) {
      this.dialogData = this.dialogDataOriginal;
      return;
    }

    this.dialogData = this.dialogDataOriginal.map(row => ({
      ...row,
      nr_cnpj_cpf_exec: row.nr_cnpj_cpf_exec ? pseudonimizar(row.nr_cnpj_cpf_exec) : row.nr_cnpj_cpf_exec,
    }));
  }

  exportExcel(): void {
    const fileNamePrefix = `Ranking-Medicos-Consultas-Eletivas-${this.operadoraNome}-${this.competenciaRange[0]}-${this.competenciaRange[1]}`;

    const headers = [
      'Ranking dos médicos com consultas eletivas',
      `${this.operadoraNome} - ${this.operadora}`,
      'Competência: ' + AgFilterUtils.competenciaRangeToString(this.competenciaRange),
      'Ano de realização: ' + this.anoBase,
      'Faixa etária: ' + this.faixasEtariasOptions.find(faixa => faixa.value == this.faixaEtaria).label,
    ];

    exportGridAsExcel({
      api: this.agGrid.api,
      headers,
      fileNamePrefix,
    });
  }

  exportExcelDialog(): void {
    const fileNamePrefix = `Ranking-Medicos-Consultas-Eletivas-Listagem-Guias-${this.operadoraNome}-${this.competenciaRange[0]}-${this.competenciaRange[1]}`;

    const headers = [
      'Ranking dos médicos com consultas eletivas / Listagem de guias',
      `${this.operadoraNome} - ${this.operadora}`,
      'Competência: ' + AgFilterUtils.competenciaRangeToString(this.competenciaRange),
      'Ano de realização: ' + this.anoBase,
      'Faixa etária: ' + this.faixasEtariasOptions.find(faixa => faixa.value == this.faixaEtaria).label,
      'Médico: ' + this.dialogSubtitle,
    ];

    exportGridAsExcel({
      api: this.agGridDialog.api,
      headers,
      fileNamePrefix,
    });
  }

  showDialogFor(cd_guia_monitor?: string) {
    // console.log('[RankingMedicos] showDialogFor', nr_cpf_profissional);
    this.selectedRow = this.medicoCpfMap[cd_guia_monitor];
    this.selectedRowIndex = this.medicoCpfList.indexOf(cd_guia_monitor);
    // console.log('[RankingMedicos] showDialogFor - index', this.selectedRowIndex);
    this.showDialog();
  }

  showDialogForNext() {
    // console.log('[RankingMedicos] showDialogForNext', this.selectedRowIndex);
    if (this.selectedRowIndex > this.medicoCpfList.length - 2) {
      console.error('Invalid index', this.selectedRowIndex + 1);
      return;
    }

    this.selectedRowIndex++;
    const cd_guia_monitor = this.medicoCpfList[this.selectedRowIndex];
    this.showDialogFor(cd_guia_monitor);
  }

  showDialogForPrev() {
    // console.log('[RankingMedicos] showDialogForPrev', this.selectedRowIndex);
    if (this.selectedRowIndex < 1) {
      console.error('Invalid index', this.selectedRowIndex - 1);
      return;
    }
    this.selectedRowIndex--;
    const cd_guia_monitor = this.medicoCpfList[this.selectedRowIndex];
    this.showDialogFor(cd_guia_monitor);
  }

  private showDialog() {
    // console.log('[RankingMedicos] showDialog');
    const { nr_cpf_profissional, nm_medico, cd_guia_monitor } = this.selectedRow;

    /*
    const newPath: any[] = [cd_critica];
    if (this.routeCritica) {
      newPath.unshift('..');
    }

    // console.log('[Critica] showDialog - navigation', newPath);
    this.router.navigate(newPath, { relativeTo: this.route });
    /**/

    if (!this.dialogDisplay) {
      setTimeout(() => this.gridService.applyVisaoInicial(GRID_DIALOG_KEY, this.agGridDialog));
    }
    this.dialogDisplay = true;

    this.agGridDialog?.api?.setGridOption('loading', true);
    this.service
      .getListagemGuias(
        this.operadora,
        this.competenciaRange[0],
        this.competenciaRange[1],
        this.anoBase,
        this.faixaEtaria,
        cd_guia_monitor,
        this.filterData,
      )
      .subscribe({
        next: items => {
          this.dialogDataOriginal = items.map(item => {
            const row = {
              ...item,
              nm_tp_rede: '',
              nm_id_situacao_registro: '',
            };

            row.nm_tp_rede = this.geralService.buildNomeDeDominio(row.tp_rede, 'MON_TIPO_REDE');
            row.nm_id_situacao_registro = this.geralService.buildNomeDeDominio(
              row.id_situacao_registro,
              'MON_REGISTRO_SITUACAO',
            );

            return row;
          });

          this.pseudonimizarDialogData();
          this.updateDialogSubtitle();

          this.dialogSubtitle = nr_cpf_profissional
            ? `${this.cpfCnpjPipe.transform(nr_cpf_profissional)} - ${nm_medico}`
            : nm_medico;
        },
        error: error => this.notificationService.handleError(error),
        complete: () => {
          this.agGrid?.api?.setGridOption('loading', false);
          this.agGridDialog?.api?.setGridOption('loading', false);
        },
      });
  }

  onDialogMaximize(event) {
    // console.log('[RankingMedicos] onDialogMaximize', event);
    this.dialogHeightPadding = event.maximized ? DIALOG_PADDING_MAX_ON : DIALOG_PADDING_MAX_OFF;
  }

  onDialogHide(event) {
    // console.log('[RankingMedicos] onDialogHide', event);
    // this.router.navigate([this.routeCritica ? '..' : '.'], { relativeTo: this.route });
  }

  showDetalheGuiaFor(cd_guia_monitor: string) {
    // console.log('showDetalheGuiaFor', cd_guia_monitor);
    this.detalheGuiaDisplay = true;
    this.detalheGuiaCodigo = cd_guia_monitor;
  }

  onCopyUrlFilters() {
    const visao = this.gridService.getCurrentVisao(GRID_KEY);
    const sort = this.gridService.getSimpleSort(this.agGrid);
    const extraFilters = this.gridService.getEncodedFilter(this.agGrid);

    const filter = {
      ...this.filterData,
      competencia: this.competenciaRange[0],
      competencia_final: this.competenciaRange[1],
      anoBase: this.anoBase,
      faixaEtaria: this.faixaEtaria,
      visao: visao?.cd_visao,
      sort,
      extraFilters,
    };

    const url = filterToQueryString(filter);
    navigator.clipboard.writeText(url);
    this.notificationService.info('Endereço com filtros copiado');
  }

  private ngDestroyed$ = new Subject<void>();
  ngOnDestroy() {
    this.ngDestroyed$.next();
    this.ngDestroyed$.complete();
  }
}

export interface Row extends RankingMedicosConsultasEletivasComVerificacao {}

export interface RowGuias extends RankingMedicosConsultasEletivasGuias {
  nm_tp_rede: string;
  nm_id_situacao_registro: string;
}
